import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import cl from 'classnames';
import { useParams } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import QRCode from 'qrcode.react';
import {
  Account,
  Company,
  HotelAbout as HotelAboutType,
  Package,
  Collection,
  AboutHeadset,
} from 'src/models';
import {
  useContextData,
  useLanguage,
  useXlsxWriter,
  XlsxTableConfig,
} from 'src/hooks';
import {
  compareString,
  currencySign,
  formatDuration,
  formatDurationHoursMinutes,
} from 'src/helper';
import { RoleEnum } from 'src/security/roles';
import { useAppDispatch } from 'src/store';
import { useCompanyById } from 'src/services/company';
import { fetchAbout } from 'src/services/about';
import { useAccount } from 'src/services/auth';
import {
  PageSwitcher,
  StickyTable,
  StickyTableConfig,
  ImageViewer,
} from 'src/components';
import {
  Button,
  Modal,
  Select,
  Checkbox,
  ClipboardController,
} from 'src/components/general';
import styles from './hotelAboutStyle.module.scss';

const ContentSection: React.FC<{
  title: string;
  isSelected: boolean;
  isTable?: boolean;
  children?: ReactNode;
  className?: string;
}> = ({ title, isSelected, isTable = false, children, className }) => {
  return (
    <div
      className={cl(
        styles.contentSection,
        isSelected && styles.isSelected,
        isTable && styles.tableSection,
        !children && styles.isLoading,
        className,
      )}
    >
      <div className={styles.csLabel}>{title}</div>
      {children || (
        <div className={styles.loading}>
          <div className={styles.loader} />
        </div>
      )}
    </div>
  );
};

type Section =
  | 'general'
  | 'qrCode'
  | 'packages'
  | 'promo'
  | 'cinema'
  | 'headsets';

export const HotelAbout = () => {
  const { hotel: companyId } = useParams() as { hotel: string };
  const {
    header: { width: headerWidth },
  } = useContextData();
  const company = useCompanyById(companyId) as Company;
  const account = useAccount() as Account;
  const dispatch = useAppDispatch();
  const { t } = useLanguage();
  const [about, setAbout] = useState<HotelAboutType | null>(null);
  const [show, setShow] = useState(false);
  const [collection, setCollection] = useState<Collection | null>(null);
  const [selectedSection, setSelectedSection] = useState<Section>('general');
  const [viewImage, setViewImage] = useState<string | null>(null);

  const qrCodeHotelRef = useRef<HTMLDivElement>(null);

  const { writeToXlsx } = useXlsxWriter<AboutHeadset>();

  const xlsFileName = useMemo(
    () =>
      t('page.hotel.about.headsets.xlsFileName', {
        companyName: company?.name,
      }),
    [t, company?.name],
  );

  const isAdmin = useMemo(
    () => account?.role === RoleEnum.admin,
    [account?.role],
  );

  const qrCodeHotel = useMemo(
    () =>
      company ? `${process.env.REACT_APP_WEB_ACCOUNT_URL}/${company.id}` : '',
    [company],
  );

  useEffect(() => {
    if (!company) {
      return;
    }

    (async () => {
      const res = await dispatch(fetchAbout({ companyId }));
      if (fetchAbout.fulfilled.match(res)) {
        setAbout(
          res.payload
            ? {
                ...res.payload,
                headsets:
                  res.payload.headsets?.map((hd) => ({
                    humanId: hd.humanId ?? 0,
                    roomLabel: hd.roomLabel ?? '---',
                    phyDeviceId: hd.phyDeviceId ?? '---',
                    apMacAddress: hd.apMacAddress ?? '---',
                  })) ?? [],
              }
            : null,
        );
      }
    })();
  }, [company, companyId, dispatch]);

  const sectionItems: { value: Section; label: string }[] = useMemo(
    () => [
      {
        value: 'general',
        label: t('page.hotel.about.tab.general'),
      },
      ...(isAdmin
        ? ([
            {
              value: 'qrCode',
              label: t('page.hotel.about.tab.qrCode'),
            },
          ] as { value: Section; label: string }[])
        : []),
      {
        value: 'packages',
        label: t('page.hotel.about.tab.packages'),
      },
      {
        value: 'promo',
        label: t('page.hotel.about.tab.promo'),
      },
      {
        value: 'cinema',
        label: t('page.hotel.about.tab.cinema'),
      },
      {
        value: 'headsets',
        label: t('page.hotel.about.tab.headsets'),
      },
    ],
    [t, isAdmin],
  );

  const firmwares = useMemo(() => {
    if (!about?.firmwares) {
      return null;
    }

    if (!about.firmwares.length) {
      return <div className={styles.noData}>{t('general.noData')}</div>;
    }

    return about.firmwares.map(
      ({ deviceTypeId, version, name, isPrivate, isActive }) => (
        <div
          className={styles.firmware}
          key={`fw-${deviceTypeId}-${version}-${name}`}
        >
          <div className={styles.fwInfoGroup}>
            <div className={styles.fwInfoItem}>
              <div className={cl(styles.fwText, styles.fwTitle)}>
                {t('page.hotel.about.firmware.type')}
              </div>
              <div className={styles.fwText}>{deviceTypeId}</div>
            </div>
            <div className={styles.fwInfoItem}>
              <div className={cl(styles.fwText, styles.fwTitle)}>
                {t('page.hotel.about.firmware.version')}
              </div>
              <div className={styles.fwText}>{version}</div>
            </div>
            <div className={styles.fwInfoItem}>
              <div className={cl(styles.fwText, styles.fwTitle)}>
                {t('page.hotel.about.firmware.name')}
              </div>
              <div className={styles.fwText}>{name}</div>
            </div>
          </div>
          {isPrivate && (
            <div className={cl(styles.tag, styles.fwPrivate)}>
              {t('page.hotel.about.firmware.private')}
            </div>
          )}
          <div
            className={cl(
              styles.tag,
              isActive ? styles.fwActive : styles.fwInactive,
            )}
          >
            {t(`page.hotel.about.firmware.${isActive ? 'active' : 'inactive'}`)}
          </div>
        </div>
      ),
    );
  }, [about?.firmwares, t]);

  const qrCodeSection = useMemo(
    () =>
      isAdmin &&
      qrCodeHotel && (
        <div className={styles.qrSection}>
          <div>
            <QRCode
              value={qrCodeHotel}
              renderAs="canvas"
              className={styles.qrCode}
            />
          </div>
          <div className={styles.qrSectionButtons}>
            <ClipboardController text={qrCodeHotel}>
              <Button
                label={t('general.copy')}
                design="lightBlue"
                size="small"
                className={styles.copyButton}
                icon="copy"
              />
            </ClipboardController>
            <ReactToPrint
              content={() => qrCodeHotelRef.current}
              trigger={() => (
                <Button
                  label={t('general.print')}
                  design="blue"
                  size="small"
                  className={styles.printButton}
                  icon="print"
                />
              )}
            />
          </div>
        </div>
      ),
    [isAdmin, qrCodeHotel, t],
  );

  const packagesConfig = useMemo<StickyTableConfig<Package>[]>(() => {
    return [
      {
        title: t('page.hotel.about.packages.name'),
        key: 'name',
        field: 'name',
        render: (name, pack) => {
          const [hours, minutes] = formatDurationHoursMinutes(pack.duration);
          return (
            <>
              {t('page.hotel.about.packages.name.render', {
                price: `${pack.price} ${currencySign(company?.location?.currency || 'rub')}`,
                hours,
              })}
              {minutes !== '00' &&
                t('page.hotel.about.packages.name.renderMin', { minutes })}
            </>
          );
        },
      },
      {
        title: t('page.hotel.about.packages.price'),
        key: 'price',
        field: 'price',
        render: (price) =>
          `${price} ${currencySign(company?.location?.currency || 'rub')}`,
      },
      {
        title: t('page.hotel.about.packages.duration'),
        key: 'duration',
        field: 'duration',
        render: (duration) => formatDuration(duration),
      },
      {
        title: 'VIP Access',
        key: 'vipAccess',
        field: 'vipAccess',
        render: (vipAccess) => <Checkbox checked={vipAccess} disabled />,
      },
      {
        title: 'Promo',
        key: 'promo',
        field: 'promo',
        render: (promo) => <Checkbox checked={promo} disabled />,
      },
    ];
  }, [company?.location?.currency, t]);

  const packagesTable = useMemo(() => {
    if (!about?.packages) {
      return null;
    }

    return (
      <StickyTable
        config={packagesConfig}
        data={about.packages}
        autoWidth
        className={styles.tableContainer}
      />
    );
  }, [about?.packages, packagesConfig]);

  const packagesTableMobile = useMemo(() => {
    if (!about?.packages) {
      return null;
    }

    return (
      <StickyTable
        config={packagesConfig}
        data={about.packages}
        className={styles.packagesContainerMobile}
      />
    );
  }, [about?.packages, packagesConfig]);

  const promoContent = useMemo(() => {
    if (!about) {
      return null;
    }

    if (!about.promoVideo) {
      return (
        <div className={styles.noData} style={{ marginBottom: 18 }}>
          {t('general.noData')}
        </div>
      );
    }

    return (
      <div
        className={cl(styles.tableContainer, styles.table, styles.flexTable)}
      >
        <div className={cl(styles.row, styles.header)}>
          <div
            className={styles.cell}
            style={{ width: '40%', maxWidth: '40%' }}
          >
            {t('page.hotel.about.promo.poster')}
          </div>
          <div className={styles.cell}>{t('page.hotel.about.promo.video')}</div>
        </div>
        <div className={cl(styles.row, styles.flexTable)}>
          <div className={cl(styles.cell, styles.flexTable)}>
            <div className={styles.flexHeader}>
              {t('page.hotel.about.promo.poster')}:
            </div>
            <img
              className={styles.img}
              src={about?.promoVideo?.image}
              alt=""
              onClick={() => {
                if (about?.promoVideo?.image) {
                  setViewImage(about.promoVideo.image);
                }
              }}
            />
          </div>
          <div className={cl(styles.cell, styles.flexTable)}>
            <div className={styles.flexHeader}>
              {t('page.hotel.about.promo.video')}:
            </div>
            <div className={styles.player}>
              <video
                src={about.promoVideo.url}
                preload="auto"
                controls
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }, [about, t]);

  const bannersContent = useMemo(() => {
    if (!about) {
      return null;
    }

    if (!about.banners || !about.banners.length) {
      return (
        <div className={styles.noData} style={{ marginBottom: 18 }}>
          {t('general.noData')}
        </div>
      );
    }

    return (
      <div
        className={cl(styles.tableContainer, styles.table, styles.flexTable)}
      >
        <div className={cl(styles.row, styles.header)}>
          <div className={styles.cell}>
            {t('page.hotel.about.promo.banner')}
          </div>
          <div className={styles.cell}>
            {t('page.hotel.about.promo.description')}
          </div>
        </div>
        {about.banners.map(({ id, images, description }) => (
          <div key={`bn-${id}`} className={cl(styles.row, styles.flexTable)}>
            <div className={cl(styles.cell, styles.flexTable)}>
              <div className={styles.flexHeader}>
                {t('page.hotel.about.promo.banner')}:
              </div>
              <div className={styles.bannerImages}>
                {!images.length && '---'}
                {images.map((image, index) => (
                  <img
                    key={`${image}-${index}`}
                    className={styles.img}
                    src={image}
                    alt=""
                    onClick={() => {
                      if (image) {
                        setViewImage(image);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
            <div className={cl(styles.cell, styles.flexTable)}>
              <div className={styles.flexHeader}>
                {t('page.hotel.about.promo.description')}:
              </div>
              <div
                className={cl(styles.cellDescription, styles.promoDescription)}
              >
                {description}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }, [about, t]);

  const cinemaContent = useMemo(() => {
    if (!about) {
      return null;
    }

    if (!about.collections) {
      return (
        <div className={styles.noData} style={{ marginBottom: 18 }}>
          {t('general.noData')}
        </div>
      );
    }

    return (
      <div className={cl(styles.tableContainer, styles.table)}>
        <div className={cl(styles.row)}>
          <div className={cl(styles.cell)}>
            {t('page.hotel.about.collections')}
          </div>
        </div>
        {about.collections.map((item) => (
          <div key={`cl-col-${item.id}`} className={cl(styles.row)}>
            <div
              className={cl(styles.cell, styles.collectionItem)}
              onClick={() => {
                setCollection(item);
                setShow(true);
              }}
            >
              {item.name}
            </div>
          </div>
        ))}
      </div>
    );
  }, [about, t]);

  const cinemaTemplate = useMemo(
    () => about?.template?.name || t('page.hotel.about.noTemplate'),
    [about?.template?.name, t],
  );

  const headsetsConfig: StickyTableConfig<AboutHeadset>[] = useMemo(
    () => [
      {
        title: t('page.hotel.about.headsets.humanId'),
        key: 'humanId',
        field: 'humanId',
        sorter: (a, b, sortOrder) =>
          (a.humanId - b.humanId) * (sortOrder === 'descend' ? -1 : 1),
      },
      {
        title: t('page.hotel.about.headsets.roomLabel'),
        key: 'roomLabel',
        field: 'roomLabel',
        sorter: (a, b, sortOrder) =>
          compareString(a.roomLabel, b.roomLabel) *
          (sortOrder === 'descend' ? -1 : 1),
      },
      {
        title: t('page.hotel.about.headsets.phyDeviceId'),
        key: 'phyDeviceId',
        field: 'phyDeviceId',
        sorter: (a, b, sortOrder) =>
          compareString(a.phyDeviceId, b.phyDeviceId) *
          (sortOrder === 'descend' ? -1 : 1),
      },
      {
        title: t('page.hotel.about.headsets.apMacAddress'),
        key: 'apMacAddress',
        field: 'apMacAddress',
        sorter: (a, b, sortOrder) =>
          compareString(a.apMacAddress, b.apMacAddress) *
          (sortOrder === 'descend' ? -1 : 1),
      },
    ],
    [t],
  );

  const headsetsXlsxConfig: XlsxTableConfig<AboutHeadset>[] = useMemo(
    () => [
      {
        title: t('page.hotel.about.headsets.humanId'),
        field: 'humanId',
        key: 'humanId',
      },
      {
        title: t('page.hotel.about.headsets.roomLabel'),
        field: 'roomLabel',
        key: 'roomLabel',
      },
      {
        title: t('page.hotel.about.headsets.phyDeviceId'),
        field: 'phyDeviceId',
        key: 'phyDeviceId',
      },
      {
        title: t('page.hotel.about.headsets.apMacAddress'),
        field: 'apMacAddress',
        key: 'apMacAddress',
      },
    ],
    [t],
  );

  const headsetsTable = useMemo(() => {
    if (!about?.headsets) {
      return null;
    }

    return (
      <>
        <StickyTable
          config={headsetsConfig}
          data={about.headsets}
          autoWidth
          className={styles.tableContainer}
        />
        <Button
          label={t('component.exportToXlsx.button')}
          design="lightBlue"
          className={styles.xlsButton}
          icon="exportToXlsx"
          onClick={() => {
            writeToXlsx(
              {
                config: headsetsXlsxConfig,
                data: about.headsets,
              },
              xlsFileName,
            );
          }}
          size="small"
        />
      </>
    );
  }, [
    about?.headsets,
    headsetsConfig,
    t,
    writeToXlsx,
    headsetsXlsxConfig,
    xlsFileName,
  ]);

  const headsetsTableMobile = useMemo(() => {
    if (!about?.headsets) {
      return null;
    }

    return (
      <>
        <StickyTable
          config={headsetsConfig}
          data={about.headsets}
          className={styles.packagesContainerMobile}
        />
        <Button
          label={t('component.exportToXlsx.button')}
          design="lightBlue"
          className={styles.xlsButtonMobile}
          icon="exportToXlsx"
          onClick={() => {
            writeToXlsx(
              {
                config: headsetsXlsxConfig,
                data: about.headsets,
              },
              xlsFileName,
            );
          }}
          size="small"
        />
      </>
    );
  }, [
    about?.headsets,
    headsetsConfig,
    t,
    writeToXlsx,
    headsetsXlsxConfig,
    xlsFileName,
  ]);

  return (
    <div className={styles.container}>
      <ImageViewer
        close={() => {
          setViewImage(null);
        }}
        src={viewImage}
      />
      <Modal
        show={show}
        className={styles.collectionsModal}
        close={() => {
          setShow(false);
          setCollection(null);
        }}
        header={collection?.name || ''}
        footer={
          <>
            <Button
              label={t('general.close')}
              onClick={() => {
                setShow(false);
                setCollection(null);
              }}
              design="lightBlue"
              autoSize
              className={styles.modalCloseButton}
            />
          </>
        }
        closeOnEsc
      >
        {collection ? (
          <div className={styles.collectionsModalContent}>
            <div className={cl(styles.tableContainer, styles.table)}>
              <div className={cl(styles.row)}>
                <div
                  className={styles.cell}
                  style={{ width: '50%', maxWidth: '50%' }}
                >
                  {t('page.hotel.about.collection.poster')}
                </div>
                <div className={styles.cell}>
                  {t('page.hotel.about.collection.name')}
                </div>
              </div>
              {collection.contentCards.map(({ id, image, name }) => (
                <div key={`cc-${id}`} className={cl(styles.row)}>
                  <div className={styles.cell}>
                    <img
                      src={image}
                      alt=""
                      className={styles.image}
                      onClick={() => {
                        if (image) {
                          setViewImage(image);
                        }
                      }}
                    />
                  </div>
                  <div className={cl(styles.cell, styles.cellDescription)}>
                    {name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Modal>
      {Boolean(qrCodeHotel && company && isAdmin) && (
        <div className={styles.printQrHidden}>
          <div ref={qrCodeHotelRef} className={styles.printQrSection}>
            <QRCode
              value={qrCodeHotel}
              renderAs="canvas"
              className={styles.printQr}
              width={256}
              height={256}
              style={{
                width: 256,
                height: 256,
              }}
            />
            <div className={styles.printQrLabel}>{company?.name}</div>
          </div>
        </div>
      )}
      <div
        className={styles.topSection}
        style={{ minWidth: headerWidth, maxWidth: headerWidth }}
      >
        <div className={styles.pageSwitcherContainer}>
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <Select
            value={selectedSection}
            items={sectionItems}
            selectItem={(section) => {
              setSelectedSection(section as Section);
            }}
            design="switcher"
            className={styles.select}
          />
        </div>
      </div>
      <ContentSection
        title={t('page.hotel.about.firmware')}
        isSelected={selectedSection === 'general'}
      >
        {firmwares}
      </ContentSection>
      {isAdmin && (
        <ContentSection
          title={t('page.hotel.about.qrCode')}
          isSelected={selectedSection === 'qrCode'}
        >
          {qrCodeSection}
        </ContentSection>
      )}
      <ContentSection
        title={t('page.hotel.about.packages')}
        isSelected={selectedSection === 'packages'}
        isTable
        className={styles.packagesContainerDesktop}
      >
        {packagesTable}
      </ContentSection>
      {selectedSection === 'packages' && packagesTableMobile}
      <ContentSection
        title={t('page.hotel.about.promo')}
        isSelected={selectedSection === 'promo'}
        isTable
      >
        {promoContent}
      </ContentSection>
      <ContentSection
        title={t('page.hotel.about.promo.banner')}
        isSelected={selectedSection === 'promo'}
        isTable
      >
        {bannersContent}
      </ContentSection>
      <ContentSection
        title={t('page.hotel.about.cinema', { template: cinemaTemplate })}
        isSelected={selectedSection === 'cinema'}
        isTable
      >
        {cinemaContent}
      </ContentSection>
      {selectedSection === 'headsets' && headsetsTableMobile}
      <ContentSection
        title={t('page.hotel.about.headsets')}
        isSelected={selectedSection === 'headsets'}
        isTable
        className={styles.packagesContainerDesktop}
      >
        {headsetsTable}
      </ContentSection>
    </div>
  );
};

export default HotelAbout;
